import gql from 'graphql-tag';
import { productFragments } from './Product';
import { unitSettingFragments, unitInvoiceSettingFragments } from './CompanySetting';

export const orderLineFragments = gql`
  fragment orderLineFragments on OrderLine {
    id
    number
    note
    productDescription
    productDescriptionEnabled
    orderedQuantity
    quantity
    unitOfMeasureCode
    priceAmount
    lineExtensionAmount
    vatRateSymbol
    taxPercent
    taxExemptionReason
    taxAmount
    product {
      ...productFragments
    }
    itemName
    itemDescription
    sellersItemIdentificationId
    project {
      id
      code
      name
    }
    department {
      id
      code
      name
    }
    car {
      id
      name
      registrationNumber
    }
    accountingCost
    orderLineAllowanceCharge {
      id
      reason
      percent
      amount
    }
  }
  ${productFragments}
`;

export const orderCustomerFragments = gql`
  fragment orderCustomerFragments on Customer {
    id
      unit {
        id
        name
        address
        zip
        city
        country {
          code
          name
        }
        mobile
        openedDate
        closedDate
        vatRegulation
        foretaksRegisteredSince
        highestPriorityIdentity {
          id
          value
          identificator {
            id
            name
          }
        }
      }
      phone
      email
      unofficialName
      customerContacts {
        id
        firstName
        lastName
      }
      deliveryLocations {
        id
        address
        additionalAddress
        city
        zip
        country {
          code
          name
        }
      }
      reference
    }
`;

export const orderAllowanceChargeFragments = gql`
  fragment orderAllowanceChargeFragments on OrderAllowanceCharge {
    id
    reason
    percent
    orderAllowanceChargeLines {
      id
      amount
      baseAmount
      vatRateSymbol
      taxPercent
    }
  }
`;

export const orderPartyFragments = gql`
  fragment orderPartyFragments on OrderParty {
    id
    name
    unofficialName
    phone
    mobile
    email
    partyIdentificationScheme
    partyIdentification
    city
    zip
    streetName
    additionalStreetName
    reference
    country {
      code
      name
    }
    countryCode
    vatRegulation
    foretaksRegisteredSince
  }
`;

export const supplierPartyVatRegulation = gql`
  fragment supplierPartyVatRegulation on OrderParty {
    id
    vatRegulation
  }
`;

export const orderDeliveryFragments = gql`
  fragment orderDeliveryFragments on OrderDelivery {
    id
    date
    streetName
    additionalStreetName
    city
    zip
    country {
      code
      name
    }
    countryCode
  }
`;

export const orderStatusFragments = gql`
  fragment orderStatusFragments on Order {
    status
    isOpen
    isLocked
    isCancelled
  }
`;

export const orderUnitSettingsDispaySettingFragments = gql`
  fragment orderUnitSettingsDispaySettingFragments on Unit {
    unitSetting {
      id
      unitInvoiceSetting {
        id
        exports
        productGroups
        paymentMethods
        taxInclusiveAmountRounding
        orderSaveLockConfirmation
        displaySettings {
          invoiceLineTaxAmount
          invoiceLineAllowanceCharge
          invoiceLineOrderedQuantity
          invoiceLineCar
          invoiceLineProject
          invoiceLineDepartment
          invoiceAllowanceCharge
          invoiceCar
          invoiceProject
          invoiceDepartment
        }
        legalNotes
        customNote
      }
    }
  }
`;

export const orderMailLogsFragments = gql`
  fragment orderMailLogsFragments on LogOrderMail {
    id pdfType recipientMail sentAt
    sentBy { id name }
    deliveryStatus
    deliveryError
  }
`;

export const orderFragments = gql`
  fragment orderFragments on Order {
    id
    unit {
      id
      name
      isCompany
      ...orderUnitSettingsDispaySettingFragments
    }
    currencyCode
    logo {
      id
      signedBlobId
      url(size: LARGE)
    }
    number
    currencyCode
    taxAmount
    lineExtensionAmount
    taxExclusiveAmount
    taxInclusiveAmount
    payableAmount
    customer {
      ...orderCustomerFragments
    }
    date
    bban
    iban
    reference
    note
    export
    project {
      id
      code
      name
    }
    department {
      id
      code
      name
    }
    car {
      id
      name
      registrationNumber
    }
    accountingCost
    supplierParty {
      ...orderPartyFragments
    }
    customerParty {
      ...orderPartyFragments
    }
    orderDelivery {
      ...orderDeliveryFragments
    }
    orderLines {
      ...orderLineFragments
    }
    orderAllowanceCharges {
      ...orderAllowanceChargeFragments
    }
    invoice {
      id number createdAt
      createdBy { id name }
    }
    savedAt
    savedBy { id name }
    cancelledAt
    cancelledBy {
      id name
    }
    lockedAt
    lockedBy {
      id
      name
    }
    ...orderStatusFragments
  }
  ${orderUnitSettingsDispaySettingFragments}
  ${orderCustomerFragments}
  ${orderPartyFragments}
  ${orderDeliveryFragments}
  ${orderLineFragments}
  ${orderAllowanceChargeFragments}
  ${orderStatusFragments}
`;

export const GET_ORDER = gql`
  query order($id: ID!) {
    order(id: $id) {
      ...orderFragments,
    }
  },
  ${orderFragments}
`;

export const UPDATE_ORDER = gql`
  mutation updateOrder(
    $id: ID!,
    $date: Date!,
    $note: String,
    $export: Boolean,
    $logo: ExistingAttachmentInput,
    $currencyCode: String!,
    $supplierParty: OrderPartyBase!,
    $customerParty: OrderPartyBase!,
    $orderDelivery: OrderDeliveryBase!,
    $bban: String,
    $iban: String,
    $reference: String,
    $taxAmount: Decimal!,
    $lineExtensionAmount: Decimal!,
    $taxExclusiveAmount: Decimal!,
    $taxInclusiveAmount: Decimal!,
    $payableAmount: Decimal!,
    $customerId: ID!,
    $carId: ID,
    $projectId: ID,
    $departmentId: ID,
    $orderLines: [OrderLineBase!],
    $orderAllowanceCharges: [OrderAllowanceChargeBase!],
  ) {
    updateOrder(
      input: {
        order: {
          id: $id,
          date: $date,
          note: $note,
          export: $export,
          logo: $logo,
          currencyCode: $currencyCode,
          supplierParty: $supplierParty,
          customerParty: $customerParty,
          orderDelivery: $orderDelivery,
          bban: $bban,
          iban: $iban,
          reference: $reference,
          taxAmount: $taxAmount,
          lineExtensionAmount: $lineExtensionAmount,
          taxExclusiveAmount: $taxExclusiveAmount,
          taxInclusiveAmount: $taxInclusiveAmount,
          payableAmount: $payableAmount,
          customerId: $customerId,
          carId: $carId,
          projectId: $projectId,
          departmentId: $departmentId,
          orderLines: $orderLines,
          orderAllowanceCharges: $orderAllowanceCharges
        }
      }
    ) {
      order {
        ...orderFragments
      }
    }
  },
  ${orderFragments}
`;

export const CREATE_ORDER = gql`
  mutation createOrder(
    $date: Date!,
    $note: String,
    $export: Boolean,
    $logo: ExistingAttachmentInput,
    $currencyCode: String!,
    $supplierParty: OrderPartyBase!,
    $customerParty: OrderPartyBase!,
    $orderDelivery: OrderDeliveryBase!,
    $bban: String,
    $iban: String,
    $reference: String,
    $taxAmount: Decimal!,
    $lineExtensionAmount: Decimal!,
    $taxExclusiveAmount: Decimal!,
    $taxInclusiveAmount: Decimal!,
    $payableAmount: Decimal!,
    $unitId: ID!,
    $customerId: ID!,
    $carId: ID,
    $projectId: ID,
    $departmentId: ID,
    $orderLines: [OrderLineBase!],
    $orderAllowanceCharges: [OrderAllowanceChargeBase!],
  ) {
    createOrder(
      input: {
        order: {
          date: $date,
          note: $note,
          export: $export,
          logo: $logo,
          currencyCode: $currencyCode,
          supplierParty: $supplierParty,
          customerParty: $customerParty,
          orderDelivery: $orderDelivery,
          bban: $bban,
          iban: $iban,
          reference: $reference,
          taxAmount: $taxAmount,
          lineExtensionAmount: $lineExtensionAmount,
          taxExclusiveAmount: $taxExclusiveAmount,
          taxInclusiveAmount: $taxInclusiveAmount,
          payableAmount: $payableAmount,
          unitId: $unitId,
          customerId: $customerId,
          carId: $carId,
          projectId: $projectId,
          departmentId: $departmentId,
          orderLines: $orderLines,
          orderAllowanceCharges: $orderAllowanceCharges
        }
      }
    ) {
      order {
        id
      }
    }
  },
`;

export const CANCEL_ORDER = gql`
  mutation cancelOrder( $orderId: ID! ) {
    cancelOrder( input: { id: $orderId } ) {
      order {
        id
        ...orderStatusFragments
        cancelledAt
        cancelledBy {
          id name
        }
      }
    }
  }
  ${orderStatusFragments}
`;

export const ORDER_NEW_DATA = gql`
  query orderNewData {
    currentUser {
      id
      name
      firstName
      lastName
      currentUnit {
        id
        name
        unofficialName
        address
        zip
        city
        isCompany
        highestPriorityIdentity {
          id
          value
          identificator {
            id
            name
          }
        }
        country {
          code
          name
        }
        phone
        mobile
        website
        email
        unitSetting {
          ...unitSettingFragments
          unitInvoiceSetting {
            ...unitInvoiceSettingFragments
          }
        }
        vatRegulation
        foretaksRegisteredSince
      }
    }
  },
  ${unitSettingFragments}
  ${unitInvoiceSettingFragments}
`;

export const ORDER_NEW_DATA_MANDATORY_SUPPLIER_FIELDS = gql`
  query orderNewDataMandatorySupplierFields {
    currentUser {
      id
      currentUnit {
        id
        email
        address
        zip
        city
      }
    }
  }
`;

